import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  updateBulkCommissions,
  getAllCommissions,
  updateCommission,
} from "../../store/slices/commissionAndRepaymentSlice";
import {
  CheckCircle,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  Edit2,
  Loader2,
  Square,
  XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  CommissionPDFData,
  generateCommissionPDF,
} from "../../pages/EPCCustomerManager/generateCommissionPDF";
import { ICommissionHistoryItem } from "../../models/commissionAndRepayment.model";
import { validateBankDetails } from "../../utils/commissionPDFUtils";
import SearchInput from "../Search/SearchInput";
import PaginationControls from "../pagination/PaginationControls";
import CommissionAdjustmentModal from "./CommissionAdjustmentModal";
import Button, { ButtonType } from "../Assets/Button";
import AddCommissionModal from "./AddCommissionModal";
import StatusFilter from "../Search/filterComponentApPNRJ";

const CommissionsTable: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();
  const commissions = useSelector(
    (state: RootState) => state.commissionAndRepayment.salesRepCommissions,
  );
  const loading = useSelector(
    (state: RootState) =>
      state.commissionAndRepayment.loadingState.fetchingCommissionsLoading,
  );
  const updatingBulk = useSelector(
    (state: RootState) =>
      state.commissionAndRepayment.loadingState.updatingBulkCommissionLoading,
  );
  const totalPages = useSelector(
    (state: RootState) => state.commissionAndRepayment.totalPages || 1,
  );

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [selectedItems, setSelectedItems] = useState<Record<string, boolean>>(
    {},
  );
  const [activeSalesRepId, setActiveSalesRepId] = useState<string | null>(null);
  const [addCommissionModalOpen, setAddCommissionModalOpen] = useState(false);
  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [isPrevLoading, setIsPrevLoading] = useState(false);
  const [itemsPerPage] = useState(10);

  const [filter, setFilter] = useState({ status: "pending" });

  const user = useSelector((state: RootState) => state.auth.userName?.username);
  const role = useSelector((state: RootState) => state.auth.role);
  const isAdminRole = role === "admin";
  const isCseOrBdeRole = role === "CSE" || role === "bde";
  const isAccountRole = role === "account";
  const [isUpdatingAdjustment, setIsUpdatingAdjustment] = useState(false);

  const [commissionDataForEdit, setCommissionDataForEdit] = useState<{
    isOpen: boolean;
    index: number;
    commissionId: string | null;
    commissionHistoryId: string | null;
    salesRepCommission?: number;
    excessAmount?: number;
    adjustmentAmount?: number;
    excessAdjustmentAmount?: number;
  }>({
    isOpen: false,
    index: 0,
    commissionId: null,
    commissionHistoryId: null,
  });

  const handleAdjustmentSubmit = async (data: {
    salesRepCommission?: number;
    excessAmount?: number;
    excessAdjustmentAmount?: number;
    adjustmentAmount?: number;
  }) => {
    if (
      !commissionDataForEdit.commissionHistoryId ||
      !commissionDataForEdit.commissionId
    ) {
      toast.error("No CommisionId/CommissionHistoryId found");
      return;
    }

    const payload: any = {};

    payload.salesRepCommission = data.salesRepCommission;

    if (data.excessAmount !== commissionDataForEdit.excessAmount) {
      payload.excessAmount = data.excessAmount;
    }

    if (data.adjustmentAmount !== commissionDataForEdit.adjustmentAmount) {
      payload.adjustmentAmount = data.adjustmentAmount;
    }
    if (
      data.excessAdjustmentAmount !==
      commissionDataForEdit.excessAdjustmentAmount
    ) {
      payload.excessAdjustAmount = data.excessAdjustmentAmount;
    }
    // nothing changed
    if (Object.keys(payload).length === 0) {
      toast.error("No changes detected");
      return;
    }

    setIsUpdatingAdjustment(true);

    try {
      await dispatch(
        updateCommission({
          commissionId: commissionDataForEdit.commissionId,
          commissionHistoryId: commissionDataForEdit.commissionHistoryId,
          data: payload,
        }),
      ).unwrap();

      await fetchCommissions(currentPage, filter);
    } catch (error) {
      toast.error("Failed to update commission adjustment");
      console.error(error);
    } finally {
      setIsUpdatingAdjustment(false);
      setCommissionDataForEdit((prev) => ({
        ...prev,
        isOpen: false,
      }));
    }
  };

  const fetchCommissions = useCallback(
    async (page: number = 1, filter: any) => {
      await dispatch(
        getAllCommissions({
          page,
          limit: itemsPerPage,
          search,
          filter,
        }),
      );
    },
    [dispatch, itemsPerPage, search],
  );

  useEffect(() => {
    fetchCommissions(currentPage, filter);
  }, [fetchCommissions, currentPage, filter]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleNextPage = async () => {
    if (currentPage >= totalPages) return;
    setIsNextLoading(true);
    const nextPage = currentPage + 1;
    await fetchCommissions(nextPage, filter);
    setCurrentPage(nextPage);
    setIsNextLoading(false);
  };

  const handlePreviousPage = async () => {
    if (currentPage <= 1) return;
    setIsPrevLoading(true);
    const prevPage = currentPage - 1;
    await fetchCommissions(prevPage, filter);
    setCurrentPage(prevPage);
    setIsPrevLoading(false);
  };

  const toggleRow = useCallback((id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const toggleItemSelection = useCallback(
    (commissionId: string, historyId: string) => {
      const key = `${commissionId}-${historyId}`;

      setSelectedItems((prev) => {
        const isCurrentlySelected = !!prev[key];

        // If selecting a NEW item
        if (!isCurrentlySelected) {
          // Another sales rep already selected
          if (activeSalesRepId && activeSalesRepId !== commissionId) {
            toast.error(
              "You can only bulk approve commissions of one Sales Representative at a time",
            );
            return prev;
          }

          // Allow selection & lock sales rep
          setActiveSalesRepId(commissionId);

          return {
            ...prev,
            [key]: true,
          };
        }

        // If deselecting
        const updated = { ...prev };
        delete updated[key];

        // If no items left → unlock sales rep
        const stillSelected = Object.keys(updated).some((k) =>
          k.startsWith(`${commissionId}-`),
        );

        if (!stillSelected) {
          setActiveSalesRepId(null);
        }

        return updated;
      });
    },
    [activeSalesRepId],
  );

  const getSelectedCommissionHistoryIds = useCallback(() => {
    const selectedIds: string[] = [];

    Object.entries(selectedItems).forEach(([key, isSelected]) => {
      if (isSelected) {
        const [, historyId] = key.split("-");
        selectedIds.push(historyId);
      }
    });

    return selectedIds;
  }, [selectedItems]);

  const getSelectableHistoryIds = useCallback(
    (commission: any) => {
      if (!Array.isArray(commission.commissionHistory)) return [];

      return commission.commissionHistory
        .filter((h: ICommissionHistoryItem) => {
          if (h.status === "approved" && h.accountStatus === "approved") {
            return false;
          }

          if (isAccountRole) {
            return h.status === "approved" && h.accountStatus === "pending";
          }

          if (isCseOrBdeRole) {
            return h.status === "pending";
          }

          if (isAdminRole) {
            return h.status === "pending" || h.accountStatus === "pending";
          }

          return false;
        })
        .map((h: ICommissionHistoryItem) => h._id);
    },
    [isAccountRole, isCseOrBdeRole, isAdminRole],
  );

  const toggleSelectAllForSalesRep = useCallback(
    (commission: any) => {
      const commissionId = commission._id;
      const selectableIds = getSelectableHistoryIds(commission);

      if (selectableIds.length === 0) return;

      setSelectedItems((prev) => {
        // prevent multi-sales-rep selection
        if (activeSalesRepId && activeSalesRepId !== commissionId) {
          toast.error(
            "You can only bulk approve commissions of one Sales Representative at a time",
          );
          return prev;
        }

        const allSelected = selectableIds.every(
          (hid: string) => prev[`${commissionId}-${hid}`],
        );

        const updated = { ...prev };

        if (allSelected) {
          // deselect all
          selectableIds.forEach((hid: string) => {
            delete updated[`${commissionId}-${hid}`];
          });

          setActiveSalesRepId(null);
        } else {
          // select all
          selectableIds.forEach((hid: string) => {
            updated[`${commissionId}-${hid}`] = true;
          });

          setActiveSalesRepId(commissionId);
        }

        return updated;
      });
    },
    [activeSalesRepId, getSelectableHistoryIds],
  );

  const getActiveSalesRepName = useCallback(() => {
    if (!activeSalesRepId) return "";

    const commission = commissions.find((c: any) => c._id === activeSalesRepId);

    return commission?.salesRepresentative?.name || "";
  }, [activeSalesRepId, commissions]);

  const getActiveSalesRepPan = useCallback(() => {
    if (!activeSalesRepId) return "";

    const commission = commissions.find((c: any) => c._id === activeSalesRepId);

    return commission?.salesRepresentative?.panNumber;
  }, [activeSalesRepId, commissions]);

  const getActiveSalesRepBankDetails = useCallback(() => {
    if (!activeSalesRepId) return null;

    const commission = commissions.find((c: any) => c._id === activeSalesRepId);

    if (!commission?.salesRepresentative) return null;

    const { bankAccountName, bankAccountNumber, ifsc } =
      commission.salesRepresentative;

    return {
      bankAccountName,
      bankAccountNumber,
      ifsc,
    };
  }, [activeSalesRepId, commissions]);

  const getSelectedCommissionData = useCallback(() => {
    const selectedData: CommissionPDFData[] = [];

    commissions.forEach((commission) => {
      if (Array.isArray(commission.commissionHistory)) {
        commission.commissionHistory.forEach((item) => {
          const key = `${commission._id}-${item._id}`;
          if (selectedItems[key]) {
            const customerName = item.customerId?.customerName;
            selectedData.push({
              commissionId: item._id || "",
              commissionAmount: item.commissionAmount || 0,
              calculatedCommission: item.calculatedCommission || 0,
              status: item.status || "pending",
              plantSize: item.quoteId?.plantSize,
              customerName,
              confirmAt: item.quoteId?.confirmAt,
              excessAmount: item.quoteId?.excessAmount,
              customCommission: item.quoteId?.customCommission,
              commissionValue: commission.salesRepresentative?.commissionValue,
              adjustmentAmount: item.quoteId?.adjustmentAmount,
              materialDeduction: item.materialDeduction,
            });
          }
        });
      }
    });

    return selectedData;
  }, [commissions, selectedItems]);

  const handleBulkAction = useCallback(
    async (action: "approve" | "reject", proofFile?: File | null) => {
      const selectedIds = getSelectedCommissionHistoryIds();

      if (selectedIds.length === 0) {
        toast.error("Please select at least one commission");
        return;
      }

      const bankDetails = getActiveSalesRepBankDetails();

      if (!bankDetails) {
        return;
      }

      const isValid = validateBankDetails(bankDetails);
      if (!isValid) {
        return;
      }

      try {
        await dispatch(
          updateBulkCommissions({
            commissionHistoryIds: selectedIds,
            action,
            proofFile,
          }),
        ).unwrap();

        if (action === "approve" && (isCseOrBdeRole || isAdminRole)) {
          const selectedData = getSelectedCommissionData();
          const salesRepName = getActiveSalesRepName();
          const panNumber = getActiveSalesRepPan();
          const bankDetails = getActiveSalesRepBankDetails();

          generateCommissionPDF(
            selectedData,
            user,
            salesRepName,
            panNumber,
            bankDetails || {},
          );
        }

        setSelectedItems({});
        setActiveSalesRepId(null);
      } catch (error) {
        console.error("Failed to update commissions:", error);
        toast.error("Failed to update commissions");
      }
    },
    [
      getSelectedCommissionHistoryIds,
      getActiveSalesRepBankDetails,
      dispatch,
      isCseOrBdeRole,
      isAdminRole,
      getSelectedCommissionData,
      getActiveSalesRepName,
      getActiveSalesRepPan,
      user,
    ],
  );

  const calculateTotals = useCallback(() => {
    let totalSelected = 0;
    let totalSelectedAmount = 0;
    let totalAllAmount = 0;
    let totalPendingCount = 0;

    commissions.forEach((commission) => {
      if (Array.isArray(commission.commissionHistory)) {
        commission.commissionHistory.forEach((item) => {
          const amount = item.calculatedCommission || 0;
          totalAllAmount += amount;

          // Count pending items
          if (item.status === "pending") {
            totalPendingCount++;
          }

          const key = `${commission._id}-${item._id}`;
          if (selectedItems[key]) {
            totalSelected++;
            totalSelectedAmount += amount;
          }
        });
      }
    });

    return {
      totalSelected,
      totalSelectedAmount,
      totalAllAmount,
      totalPendingCount,
    };
  }, [commissions, selectedItems]);

  const { totalSelected, totalSelectedAmount } = calculateTotals();

  const getStatusBadge = (status: string, approver?: string) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";

    if (status === "approved") {
      return (
        <span
          className={`${baseClasses} bg-emerald-100 text-emerald-800 border border-emerald-200`}
        >
          <CheckCircle className="w-3 h-3 mr-1" />
          Approved {approver && `by ${approver}`}
        </span>
      );
    } else if (status === "rejected") {
      return (
        <span
          className={`${baseClasses} bg-red-100 text-red-800 border border-red-200`}
        >
          <XCircle className="w-3 h-3 mr-1" />
          Rejected {approver && `by ${approver}`}
        </span>
      );
    } else {
      return (
        <span
          className={`${baseClasses} bg-amber-100 text-amber-800 border border-amber-200`}
        >
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          Pending {approver && `by ${approver}`}
        </span>
      );
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-5 overflow-x-hidden">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <SearchInput
          placeholder="Search Sales Representative Or Customer…"
          className="w-full pr-4 py-2 bg-white border border-gray-200 rounded-md
        focus:ring-1 focus:ring-navyBlue-primary focus:border-navyBlue-primary
        outline-none transition-all text-sm text-gray-700
        placeholder:text-gray-400 shadow-sm"
          onSearch={handleSearch}
        />

        <StatusFilter
          onChange={(value) => {
            setFilter((prev) => {
              return { ...prev, status: value };
            });
          }}
        />
        {role === "admin" && (
          <Button
            type={ButtonType.Submit}
            onClick={() => setAddCommissionModalOpen(true)}
            className="text-sm flex items-center gap-2"
          >
            Add Commission
          </Button>
        )}
      </div>
      {/* Bulk Actions Bar */}
      {totalSelected > 0 && (
        <div className="mb-4 p-3 bg-gradient-to-r from-sky-50 to-white border border-sky-100 rounded-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="text-sm text-sky-700">
              <span className="font-medium">
                {totalSelected} item(s) selected
              </span>
              {totalSelectedAmount > 0 && (
                <span className="ml-0 sm:ml-4 mt-1 sm:mt-0 block sm:inline-block text-slate-600">
                  Total: ₹{totalSelectedAmount.toLocaleString("en-IN")}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => handleBulkAction("approve")}
                disabled={updatingBulk}
                className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md text-xs font-semibold shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                  updatingBulk
                    ? "bg-emerald-200 cursor-not-allowed text-white"
                    : "bg-emerald-600 hover:bg-emerald-700 text-white"
                }`}
              >
                <CheckCircle className="w-4 h-4" />
                {updatingBulk ? "Processing..." : "Approve"}
              </button>

              <button
                onClick={() => handleBulkAction("reject")}
                disabled={updatingBulk}
                className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md text-xs font-semibold shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                  updatingBulk
                    ? "bg-red-200 cursor-not-allowed text-white"
                    : "bg-red-600 hover:bg-red-700 text-white"
                }`}
              >
                <XCircle className="w-4 h-4" />
                {updatingBulk ? "Processing..." : "Reject"}
              </button>

              <button
                onClick={() => {
                  setSelectedItems({});
                  setActiveSalesRepId(null);
                }}
                className="px-3 py-2 text-xs text-slate-600 hover:text-slate-800 border border-slate-300 hover:border-slate-500 rounded-md transition-all"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full overflow-x-scroll">
        <table className="min-w-[800px] md:min-w-full w-full divide-y divide-slate-100">
          <thead>
            <tr className="text-left text-sm text-slate-500">
              <th className="px-4 py-3">Sales Rep</th>
              <th className="px-4 py-3">Mobile</th>
              <th className="px-4 py-3">Commission Count</th>
              <th className="px-4 py-3">Total Commission</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-slate-100">
            {commissions && commissions.length > 0 ? (
              commissions.map((c: any) => {
                const rep = c.salesRepresentative || {};

                const commissionCount = Array.isArray(c.commissionHistory)
                  ? c.commissionHistory.length
                  : 0;

                const totalCommission = Array.isArray(c.commissionHistory)
                  ? c.commissionHistory.reduce(
                      (sum: number, item: any) =>
                        sum +
                        (item.calculatedCommission || 0) -
                        (item.materialDeduction || 0),
                      0,
                    )
                  : 0;

                const id =
                  c._id || rep._id || Math.random().toString(36).slice(2, 9);

                const commissionItems = Array.isArray(c.commissionHistory)
                  ? c.commissionHistory
                  : [];

                return (
                  <React.Fragment key={id}>
                    <tr
                      onClick={() => commissionCount > 0 && toggleRow(id)}
                      className={`cursor-pointer transition ${
                        commissionCount > 0
                          ? "hover:bg-slate-50"
                          : "cursor-not-allowed opacity-60"
                      }`}
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSelectAllForSalesRep(c);
                            }}
                            className="p-1 rounded-md hover:bg-slate-100"
                            title="Select all commissions for this Sales Rep"
                          >
                            {getSelectableHistoryIds(c).length > 0 &&
                            getSelectableHistoryIds(c).every(
                              (hid: string) => selectedItems[`${c._id}-${hid}`],
                            ) ? (
                              <CheckSquare className="w-5 h-5 text-sky-600" />
                            ) : (
                              <Square className="w-5 h-5 text-slate-300 hover:text-slate-500" />
                            )}
                          </button>
                          <div className="text-sm font-semibold text-slate-800">
                            {rep.name || "-"}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {rep.mobileNumber || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {commissionCount}
                      </td>
                      <td className="px-4 py-4 text-sm font-semibold text-slate-800">
                        ₹ {totalCommission.toLocaleString("en-IN")}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        <div className="flex items-center justify-center w-8 h-8 rounded-md transition text-slate-600 hover:bg-slate-100 hover:text-slate-800">
                          {expanded[id] ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Row */}
                    {expanded[id] && commissionItems.length > 0 && (
                      <tr>
                        <td colSpan={7} className="bg-slate-50 px-4 py-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {commissionItems.map(
                              (h: ICommissionHistoryItem, idx: number) => {
                                const historyId = h._id || `history-${idx}`;
                                const key = `${id}-${historyId}`;
                                const isSelected = !!selectedItems[key];

                                const customerName =
                                  h.customerId && h.customerId.customerName;

                                const amount =
                                  h.calculatedCommission -
                                  (h.materialDeduction || 0);
                                const status = h.status;
                                const accountStatus = h.accountStatus;
                                const isEditable =
                                  role === "admin" || role === "bdm";

                                const canShowCheckbox =
                                  (isCseOrBdeRole && h.status === "pending") ||
                                  (isCseOrBdeRole && h.status === "rejected") ||
                                  (isAccountRole &&
                                    (h.accountStatus === "pending" ||
                                      h.accountStatus === "rejected")) ||
                                  (isAdminRole &&
                                    (h.status === "pending" ||
                                      h.accountStatus === "pending"));

                                return (
                                  <div
                                    key={historyId}
                                    className="flex flex-col rounded-lg border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                                  >
                                    <div className="flex items-center justify-between p-1 bg-gray-50 border-b border-slate-200">
                                      <div className="flex items-center gap-2">
                                        {canShowCheckbox && (
                                          <button
                                            onClick={() =>
                                              toggleItemSelection(id, historyId)
                                            }
                                            className="p-1 rounded-md hover:bg-slate-200 focus:outline-none transition-colors"
                                            title={
                                              isSelected
                                                ? "Deselect"
                                                : "Select for approval"
                                            }
                                          >
                                            {isSelected ? (
                                              <CheckSquare className="w-5 h-5 text-sky-600" />
                                            ) : (
                                              <Square className="w-5 h-5 text-slate-400 hover:text-slate-600" />
                                            )}
                                          </button>
                                        )}
                                      </div>

                                      {isEditable && (
                                        <button
                                          onClick={() => {
                                            setCommissionDataForEdit({
                                              isOpen: true,
                                              index: idx,
                                              commissionId: c._id,
                                              commissionHistoryId: historyId,
                                              salesRepCommission:
                                                c?.salesRepresentative
                                                  ?.commissionValue || 0,
                                              excessAdjustmentAmount:
                                                h.adjustAmount || 0,
                                              excessAmount:
                                                h.quoteId?.excessAmount || 0,
                                              adjustmentAmount:
                                                h.quoteId?.adjustmentAmount ||
                                                0,
                                            });
                                          }}
                                          className="p-1 rounded-md hover:bg-slate-200 focus:outline-none transition-colors"
                                          title="Edit adjustment"
                                        >
                                          <Edit2 className="w-4 h-4 text-slate-500 hover:text-blue-600" />
                                        </button>
                                      )}
                                    </div>

                                    <div className="p-4 space-y-3">
                                      <div>
                                        <h4 className="font-semibold text-slate-800 text-base break-words">
                                          {customerName}
                                          <span className="text-slate-500 font-normal ml-1">
                                            ({h.quoteId.plantSize} KW)
                                          </span>
                                        </h4>
                                      </div>

                                      <div className="space-y-2">
                                        {/* Normal Commission */}
                                        {!h.quoteId?.customCommissionType &&
                                          !h.quoteId?.excessAmount && (
                                            <>
                                              {c.salesRepresentative
                                                ?.commissionType ===
                                              "per_kw" ? (
                                                <div className="flex justify-between items-center text-sm">
                                                  <span className="text-slate-500">
                                                    Commission (per kW):
                                                  </span>
                                                  <span className="font-medium text-slate-700">
                                                    ₹ {h.commissionAmount}
                                                  </span>
                                                </div>
                                              ) : (
                                                <div className="flex justify-between items-center text-sm">
                                                  <span className="text-slate-500">
                                                    Commission (per Order):
                                                  </span>
                                                  <span className="font-medium text-slate-700">
                                                    ₹ {h.commissionAmount}
                                                  </span>
                                                </div>
                                              )}
                                            </>
                                          )}

                                        {/* Custom Commission */}
                                        {h.quoteId?.customCommissionType && (
                                          <>
                                            <div className="flex justify-between items-center text-sm">
                                              <span className="text-slate-500">
                                                Commission (
                                                {
                                                  c.salesRepresentative
                                                    .commissionType
                                                }
                                                ):
                                              </span>
                                              <span className="font-medium text-slate-700">
                                                ₹{" "}
                                                {
                                                  c.salesRepresentative
                                                    ?.commissionValue
                                                }
                                              </span>
                                            </div>
                                            <div className="flex justify-between items-center text-sm">
                                              <span className="text-slate-500">
                                                Total
                                                {c.salesRepresentative
                                                  ?.commissionType ===
                                                  "per_kw" && (
                                                  <>
                                                    {" "}
                                                    (
                                                    {h.salesRepCommission ||
                                                      c.salesRepresentative
                                                        ?.commissionValue}{" "}
                                                    × {h.quoteId?.plantSize} kW)
                                                  </>
                                                )}
                                                :
                                              </span>
                                              <span className="font-semibold text-slate-800">
                                                ₹ {h.commissionAmount || 0}
                                              </span>
                                            </div>
                                            <div className="flex justify-between items-center text-sm">
                                              <span className="text-slate-500">
                                                Deduction:
                                              </span>
                                              <span className="font-medium text-red-500">
                                                - ₹{" "}
                                                {h.quoteId.customCommission ||
                                                  0}
                                              </span>
                                            </div>

                                            {h.materialDeduction !==
                                              undefined &&
                                              h.materialDeduction > 0 && (
                                                <div className="flex justify-between items-center text-sm">
                                                  <span className="text-slate-500">
                                                    Material Deduction:
                                                  </span>
                                                  <span className="font-medium text-red-500">
                                                    - ₹{" "}
                                                    {h.materialDeduction || 0}
                                                  </span>
                                                </div>
                                              )}
                                            {h.adjustAmount !== undefined &&
                                              h.adjustAmount !== 0 && (
                                                <div
                                                  className={`${h.adjustAmount > 0 ? "bg-green-50" : "bg-red-50"} flex justify-between items-center text-sm p-2 rounded-md -mx-1`}
                                                >
                                                  <span
                                                    className={`font-medium ${h.adjustAmount > 0 ? "text-green-600" : "text-red-600"}`}
                                                  >
                                                    Excess Adjustment Amount:
                                                  </span>
                                                  <span
                                                    className={`font-bold ${h.adjustAmount > 0 ? "text-green-600" : "text-red-600"}`}
                                                  >
                                                    ₹ {h.adjustAmount}
                                                  </span>
                                                </div>
                                              )}
                                          </>
                                        )}

                                        {/* Excess Amount */}
                                        {h.quoteId?.excessAmount && (
                                          <>
                                            <div className="flex justify-between items-center text-sm">
                                              <span className="text-slate-500">
                                                Total
                                                {c.salesRepresentative
                                                  ?.commissionType ===
                                                  "per_kw" && (
                                                  <>
                                                    {" "}
                                                    (
                                                    {h.salesRepCommission ||
                                                      c.salesRepresentative
                                                        ?.commissionValue}{" "}
                                                    × {h.quoteId?.plantSize} kW)
                                                  </>
                                                )}
                                                :
                                              </span>
                                              <span className="font-semibold text-slate-800">
                                                ₹ {h.commissionAmount || 0}
                                              </span>
                                            </div>
                                            <div className="flex justify-between items-center text-sm">
                                              <span className="text-slate-500">
                                                Excess Amount:
                                              </span>
                                              <span className="font-medium text-green-600">
                                                + ₹ {h.quoteId.excessAmount}
                                              </span>
                                            </div>
                                            <p className="text-xs text-red-500 mt-2">
                                              * Amount shown is post 8.9% GST
                                              deduction
                                            </p>
                                            {h.adjustAmount !== undefined &&
                                              h.adjustAmount !== 0 && (
                                                <div className="flex justify-between items-center text-sm">
                                                  <span className="text-slate-500">
                                                    Excess Adjustment Amount:
                                                  </span>
                                                  <span
                                                    className={`font-medium ${h.adjustAmount > 0 ? "text-green-600" : "text-red-500"} `}
                                                  >
                                                    ₹ {h.adjustAmount}
                                                  </span>
                                                </div>
                                              )}
                                          </>
                                        )}

                                        {/* Adjustment Amount */}
                                        {h.quoteId?.adjustmentAmount && (
                                          <div className="flex justify-between items-center text-sm bg-red-50 p-2 rounded-md -mx-1">
                                            <span className="text-red-600 font-medium">
                                              Adjustment Amount:
                                            </span>
                                            <span className="font-bold text-red-600">
                                              ₹ {h.quoteId?.adjustmentAmount}
                                            </span>
                                          </div>
                                        )}
                                      </div>

                                      {/* Total Amount & Status */}
                                      <div className="pt-3 border-t border-slate-100">
                                        <div className="flex justify-between items-center mb-2">
                                          <span className="text-slate-500 text-sm">
                                            Total Amount:
                                          </span>
                                          <span className="font-bold text-lg text-slate-800">
                                            ₹{amount.toLocaleString("en-IN")}
                                          </span>
                                        </div>

                                        <div className="flex gap-2 justify-between flex-wrap">
                                          {getStatusBadge(
                                            status,
                                            h.cseId?.username,
                                          )}
                                          {getStatusBadge(
                                            accountStatus || "pending",
                                            "Account",
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-6 text-center text-slate-500"
                >
                  {loading ? "Loading commissions..." : "No commissions found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {!loading && (
          <div className="mt-4">
            <PaginationControls
              isNextFetching={isNextLoading}
              isPrevFetching={isPrevLoading}
              currentPage={currentPage}
              totalPages={totalPages}
              onNext={handleNextPage}
              onPrev={handlePreviousPage}
            />
          </div>
        )}
      </div>
      <CommissionAdjustmentModal
        isOpen={commissionDataForEdit.isOpen}
        onClose={() =>
          setCommissionDataForEdit((prev) => ({ ...prev, isOpen: false }))
        }
        onSubmit={handleAdjustmentSubmit}
        amounts={{
          salesRepCommission: commissionDataForEdit.salesRepCommission,
          excessAdjustmentAmount: commissionDataForEdit.excessAdjustmentAmount,
          excessAmount: commissionDataForEdit.excessAmount,
          adjustmentAmount: commissionDataForEdit.adjustmentAmount,
        }}
        isLoading={isUpdatingAdjustment}
      />
      <AddCommissionModal
        open={addCommissionModalOpen}
        onClose={() => setAddCommissionModalOpen(false)}
      />
    </div>
  );
};

export default CommissionsTable;
